import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, CreditCard, Mail, ShoppingCart, Users, BarChart } from 'lucide-react';

const IntegrationsSection: React.FC = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Seamless Integrations</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our platform connects with your favorite tools and services, making it easy to incorporate referral marketing into your existing business workflow.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* E-commerce Integrations */}
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <ShoppingCart className="h-8 w-8 text-primary-blue mb-2" />
              <CardTitle>E-commerce Platforms</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Seamlessly connect with popular e-commerce platforms to track purchases from referred customers and automatically trigger rewards.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Shopify</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">WooCommerce</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">BigCommerce</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Magento</div>
              </div>
            </CardContent>
          </Card>
          
          {/* CRM Integrations */}
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <Users className="h-8 w-8 text-primary-blue mb-2" />
              <CardTitle>CRM Systems</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Connect with customer relationship management tools to track referral leads and maintain a unified view of customer interactions.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Salesforce</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">HubSpot</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Zoho CRM</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Pipedrive</div>
              </div>
            </CardContent>
          </Card>
          
          {/* Email Marketing Integrations */}
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <Mail className="h-8 w-8 text-primary-blue mb-2" />
              <CardTitle>Email Marketing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Integrate with email marketing platforms to automate referral invitations, follow-ups, and reward notifications.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Mailchimp</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Klaviyo</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Campaign Monitor</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">ActiveCampaign</div>
              </div>
            </CardContent>
          </Card>
          
          {/* Payment Integrations */}
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CreditCard className="h-8 w-8 text-primary-blue mb-2" />
              <CardTitle>Payment Systems</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Connect with payment processors to streamline reward payouts and manage financial transactions for your referral program.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Stripe</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">PayPal</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Square</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Tremendous</div>
              </div>
            </CardContent>
          </Card>
          
          {/* Analytics Integrations */}
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <BarChart className="h-8 w-8 text-primary-blue mb-2" />
              <CardTitle>Analytics Platforms</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Connect with analytics tools to gain deeper insights into your referral program performance and customer behavior.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Google Analytics</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Mixpanel</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Amplitude</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Segment</div>
              </div>
            </CardContent>
          </Card>
          
          {/* Data & Accounting Integrations */}
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <Database className="h-8 w-8 text-primary-blue mb-2" />
              <CardTitle>Data & Accounting</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Connect with accounting and data management tools to streamline financial reporting and data synchronization.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">QuickBooks</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Xero</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Google Sheets</div>
                <div className="bg-gray-50 py-2 px-3 rounded-md text-center text-sm">Zapier</div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="text-center mt-12">
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Don't see your favorite tool? Our platform offers a robust API and webhook system 
            that allows for custom integrations with virtually any service.
          </p>
        </div>
      </div>
    </section>
  );
};

export default IntegrationsSection;
